import { useEffect, useRef, useState } from 'react';

import { Keys, getSystem } from '@/system';

export const useCursorDetection = () => {
  const [isUsingCursor, setIsUsingCursor] = useState(false);
  const [hasPointerInteractions, setHasPointerInteractions] = useState(false);
  const lastPointerTime = useRef<number>(0);
  const pointerMoveCount = useRef<number>(0);
  const cursorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const system = getSystem();
  let interval: NodeJS.Timeout;

  useEffect(() => {
    if (system.id === 'xfinity') {
      setHasPointerInteractions(false);
      setIsUsingCursor(false);
      return;
    }

    const handlePointerMove = () => {
      const now = Date.now();
      lastPointerTime.current = now;
      pointerMoveCount.current += 1;
      setHasPointerInteractions(true);

      // frequent pointer movements ~= using cursor-based remote
      if (pointerMoveCount.current > 5) {
        setIsUsingCursor(true);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if it's a directional key
      const keyPressed = system.key(event);
      const directionalKeys = [Keys.UP, Keys.DOWN, Keys.LEFT, Keys.RIGHT];
      const isDirectionalKey = directionalKeys.includes(keyPressed);

      if (isDirectionalKey) {
        // disable cursor mode
        setIsUsingCursor(false);
        pointerMoveCount.current = 0;

        // Clear any existing timeout
        if (cursorTimeoutRef.current) {
          clearTimeout(cursorTimeoutRef.current);
          cursorTimeoutRef.current = null;
        }
      } else {
        // For non-directional keys, reset cursor detection after a delay
        if (cursorTimeoutRef.current) {
          clearTimeout(cursorTimeoutRef.current);
        }

        cursorTimeoutRef.current = setTimeout(() => {
          if (Date.now() - lastPointerTime.current > 5000) {
            setIsUsingCursor(false);
            pointerMoveCount.current = 0;
          }
          cursorTimeoutRef.current = null;
        }, 2000);
      }
    };

    // Check for pointer interactions periodically
    const checkPointerActivity = () => {
      const timeSinceLastPointer = Date.now() - lastPointerTime.current;

      // If no pointer activity for 10 seconds, assume not using cursor
      if (timeSinceLastPointer > 10000) {
        setIsUsingCursor(false);
        pointerMoveCount.current = 0;
      }
    };

    if (system.hasPointer) {
      interval = setInterval(checkPointerActivity, 5000);

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (system.hasPointer) {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('keydown', handleKeyDown);

        if (interval) clearInterval(interval);

        if (cursorTimeoutRef.current) {
          clearTimeout(cursorTimeoutRef.current);
          cursorTimeoutRef.current = null;
        }
      }
    };
  }, []);

  return {
    isUsingCursor,
    hasPointerInteractions,
    systemId: system.id,
  };
};
